import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { useUpdateProtocolInstance } from 'client/app/apps/protocols/api/ProtocolsAPI';
import { ProtocolStepParameterInfo } from 'client/app/apps/protocols/lib/types';
import { ProtocolInstanceQuery } from 'client/app/gql';
import { ProtocolStepInput } from 'common/types/Protocol';
import { getElementId, Path } from 'common/types/schema';

type FetchedProtocolInstance = NonNullable<
  ProtocolInstanceQuery['protocolInstance']['instance']
>;

type ProtocolInstanceContextType = {
  loading: boolean;
  /** if set, provides a user facing reason for why data is being re-fetched */
  refetchingReason?: string;
  protocolInstance: FetchedProtocolInstance;
  updateProtocolInput: (input: ProtocolStepInput, value: any) => void;
  getProtocolStepParameterInfo: (input: ProtocolStepInput) => ProtocolStepParameterInfo;
};

export const ProtocolInstanceContext = createContext<
  ProtocolInstanceContextType | undefined
>(undefined);

type ProtocolInstanceProviderProps = {
  instance: FetchedProtocolInstance;
  getPathConfigDeviceIds: (path: Path) => string[];
} & PropsWithChildren;

export const useProtocolInstanceContext = () => {
  const context = useContext(ProtocolInstanceContext);

  if (context === undefined) {
    throw new Error(
      'useProtocolInstanceContext must be used within a ProtocolInstanceProvider',
    );
  }

  return context;
};

export const ProtocolInstanceProvider: FC<ProtocolInstanceProviderProps> = ({
  instance,
  children,
  getPathConfigDeviceIds,
}) => {
  const { loading, refetchingReason, handleUpdateProtocolInstance } =
    useUpdateProtocolInstance(instance.id);

  const schemaInputsById = useMemo(() => {
    const schemaInputs = instance.protocol.workflow.workflow.Schema?.inputs ?? [];
    return Object.fromEntries(schemaInputs.map(input => [input.id, input]));
  }, [instance.protocol.workflow.workflow.Schema]);

  const updateProtocolInput = useCallback(
    async (input: ProtocolStepInput, value: any) => {
      // important to set null so the protocol input key is retained and set with
      // a null value during serialisation, otherwise the value is not deleted
      // from the perspective of validating instance updates
      const newValue = value ? value : null;
      const linkedUpdates = input.linked?.reduce((result, link) => {
        return { ...result, [link.id]: newValue };
      }, {});

      await handleUpdateProtocolInstance({
        editVersion: instance.editVersion,
        params: {
          ...instance.parameters,
          ...linkedUpdates,
          [input.id]: newValue,
        },
      });
    },
    [handleUpdateProtocolInstance, instance.editVersion, instance.parameters],
  );

  const getProtocolStepParameterInfo = (
    input: ProtocolStepInput,
  ): ProtocolStepParameterInfo => {
    const path = schemaInputsById[input.id].path;
    return {
      parameter: input,
      value: instance.parameters[input.id],
      elementInstanceId: getElementId(path),
      relatedConfiguredDeviceIds: getPathConfigDeviceIds(path),
    };
  };

  const state: ProtocolInstanceContextType = {
    loading,
    protocolInstance: instance,
    getProtocolStepParameterInfo,
    updateProtocolInput,
    refetchingReason,
  };

  return (
    <ProtocolInstanceContext.Provider value={state}>
      {children}
    </ProtocolInstanceContext.Provider>
  );
};
